import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Signposts from './components/Signposts';
import {SS_HEAT, SS_HERO, ConnectWithWatch, SS_SHARE} from './components/ProductVisuals';
import AppDownloadSection from './components/AppDownloadSection';

/*
 * /steadystride — rebuilt 4 Sep 2026 as a product page: features, then a handover.
 *
 * The 3 Sep version gave each persona a section, which is the landing page's job
 * done twice. Before that it was 88% the same file as /individuals. Features are
 * universal so they are stated once; context is per persona so it lives on the
 * persona page, and the signpost block is the whole handover.
 *
 * Three features: the programme and the diary it sits in, what feeds it, and
 * Sharing Codes.
 *
 * Load Rate (LRE) leads the hero. It is a product term describing what the data
 * shows, never a claim about what it proves, and no biomarker construction
 * appears. An earlier draft opened "We watch how you load", which made the
 * platform the subject and the reader the object; it now opens on the programme
 * fitting the reader's week.
 *
 * Sharing Codes are named properly and are NOT described as anonymised: a viewer
 * gets full data, read-only and time-limited. Durations are not stated because
 * the spec I could read is an archived plan; the current Sharing Codes.docx in
 * PO - CMPX should confirm them before numbers go in.
 */

const SteadyStridePage = () => {
    useEffect(() => {
        document.title = 'SteadyStride | PhysioActivity';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', 'The app your physiotherapy programme lives in, and the movement data that keeps it current.');
    }, []);

    return (
    <div className="pa2">

        <section className="hero" id="top">
            <div className="wrap two">
                <div className="heromain">
                    <h1 className="on-dark">
                        <span className="setup">A programme that fits</span>
                        <span className="payoff">the week you actually have.</span>
                    </h1>
                    <p className="lede">
                        The phone you already carry reads how much loading your week puts through
                        you, as <Link to="/how-it-works"><b>Load Rate (LRE)</b></Link>. Your
                        physiotherapist writes to that pattern, not to a general target.
                    </p>
                    <p className="nerve">
                        A heavy week eases off. A quiet week builds back up. The plan bends to
                        your life rather than asking your life to bend to it.
                    </p>
                    <div className="cta-row">
                        <a className="btn btn-t" href="#get">Get the app</a>
                    </div>
                </div>
                <div className="herovis">
                    {SS_HEAT}
                    <p className="cap">Illustrative. Not a real programme.</p>
                </div>
            </div>
        </section>

        <section className="white" id="programme">
            <div className="wrap">
                <div className="featrow rev">
                    <div className="ftext">
                        <h2>
                            <span className="setup">A smart diary,</span>
                            <span className="payoff">and the programme in it.</span>
                        </h2>
                        <p className="lede">
                            Sessions, notes, how something felt, what you actually did. The
                            programme sits in the same diary, so <b>what was prescribed and what
                            happened are one record</b>.
                        </p>
                        <ul className="fpts">
                            <li>Written by a named clinician, with their registration number</li>
                            <li>Notes, sessions and activities in the same diary as the plan</li>
                            <li>Adjustments reach you the same day</li>
                        </ul>
                    </div>
                    <div className="fvis">
                        {SS_HERO}
                        <p className="cap">Illustrative.</p>
                    </div>
                </div>
            </div>
        </section>

        <section className="tint" id="connect">
            <div className="wrap">
                <div className="featrow">
                    <div className="ftext">
                        <h2>
                            <span className="setup">Connect what</span>
                            <span className="payoff">you already wear.</span>
                        </h2>
                        <p className="lede">
                            The phone captures movement on its own. A watch adds <b>heart rate
                            and sleep</b>, which is what turns a step count into a picture of
                            effort. It all lands in <Link to="/cmpx">CMPX</Link>.
                        </p>
                        <ul className="fpts">
                            <li>Apple Health, Health Connect, Samsung Health, Garmin, Fitbit</li>
                            <li>Sensors cover the movement; you log what they cannot see</li>
                            <li>Works without any of them, on the phone alone</li>
                        </ul>
                    </div>
                    <div className="fvis">
                        {ConnectWithWatch}
                        <p className="cap">Illustrative.</p>
                    </div>
                </div>
            </div>
        </section>

        <section className="white" id="share">
            <div className="wrap">
                <div className="featrow rev">
                    <div className="ftext">
                        <h2>
                            <span className="setup">Sharing Codes.</span>
                            <span className="payoff">You decide who, and for how long.</span>
                        </h2>
                        <p className="lede">
                            Generate a Sharing Code and whoever you give it to sees your record,
                            <b> read-only and for a limited time</b>. Useful in a GP appointment,
                            or with any clinician.
                        </p>
                        <ul className="fpts">
                            <li>You generate the Sharing Code, and only you</li>
                            <li>They can look, not change</li>
                            <li>Time-limited and single use, so it does not stay open</li>
                        </ul>
                    </div>
                    <div className="fvis">
                        {SS_SHARE}
                        <p className="cap">Illustrative. Not a real code.</p>
                    </div>
                </div>
            </div>
        </section>

        <Signposts />

        <section className="s-promise" id="get">
            <div className="wrap">
                <h2 className="on-dark">
                    <span className="setup">Start where you are.</span>
                    <span className="payoff">Not where you should be.</span>
                </h2>
                <p className="lede">
                    Your first programme is built from how you actually move this week, not from
                    a questionnaire about how you usually feel.
                </p>
                <AppDownloadSection appId="steadystride" />
                <p className="quiet">
                    If your employer might cover this,{' '}
                    <Link to="/individuals#employer">check before you start</Link>. Not sure
                    where you fit? <Link to="/contact?who=individual">Talk to us</Link>.
                </p>
            </div>
        </section>

    </div>
    );
};

export default SteadyStridePage;
